import { useCallback, useMemo } from 'react';

import { useSearchParams } from 'react-router-dom';

import { hasActiveFilters, parseTableFilters, serializeFilterValue } from '#lib/tableFilters';

/**
 * Table filters + page in the URL. Any filter change drops the page so the user lands on
 * page 1 of the new result.
 *
 * @param {Record<string, object>} schema - See lib/tableFilters
 * @param {Object} [options]
 * @param {string} [options.namespace] - Prefix for every param (several tables on one page)
 * @returns {{
 *   filters: Object, filtersKey: string, isFiltered: boolean,
 *   setFilters: (partial: Object) => void, removeFilters: (keys: string[]) => void, clearFilters: () => void,
 *   page: number, setPage: (page: number) => void,
 *   showUnfilteredPage: (page: number) => void - clears every filter and opens `page` in one URL update,
 * }}
 */
export function useUrlFilters(schema, { namespace } = {}) {
	const [searchParams, setSearchParams] = useSearchParams();
	const prefix = namespace ? `${namespace}.` : '';
	const pageParam = `${prefix}page`;

	const filters = useMemo(() => parseTableFilters(searchParams, schema, prefix), [searchParams, schema, prefix]);
	const filtersKey = JSON.stringify(filters);
	const page = Math.max(1, Number.parseInt(searchParams.get(pageParam), 10) || 1);

	const update = useCallback(
		(mutate, { replace = false } = {}) =>
			setSearchParams(
				(prev) => {
					const next = new URLSearchParams(prev);
					mutate(next);
					return next;
				},
				{ replace },
			),
		[setSearchParams],
	);

	const setFilters = useCallback(
		(partial) =>
			update((next) => {
				for (const [key, value] of Object.entries(partial)) {
					const serialized = serializeFilterValue(value);
					if (serialized === null) next.delete(`${prefix}${key}`);
					else next.set(`${prefix}${key}`, serialized);
				}
				next.delete(pageParam);
			}),
		[update, prefix, pageParam],
	);

	const removeFilters = useCallback(
		(keys) =>
			update((next) => {
				for (const key of keys) next.delete(`${prefix}${key}`);
				next.delete(pageParam);
			}),
		[update, prefix, pageParam],
	);

	const clearFilters = useCallback(
		() =>
			update((next) => {
				for (const key of Object.keys(schema)) next.delete(`${prefix}${key}`);
				next.delete(pageParam);
			}),
		[update, schema, prefix, pageParam],
	);

	const setPage = useCallback(
		(nextPage) =>
			update(
				(next) => {
					if (nextPage <= 1) next.delete(pageParam);
					else next.set(pageParam, String(nextPage));
				},
				{ replace: true },
			),
		[update, pageParam],
	);

	const showUnfilteredPage = useCallback(
		(nextPage) =>
			update(
				(next) => {
					for (const key of Object.keys(schema)) next.delete(`${prefix}${key}`);
					if (nextPage <= 1) next.delete(pageParam);
					else next.set(pageParam, String(nextPage));
				},
				{ replace: true },
			),
		[update, schema, prefix, pageParam],
	);

	return {
		filters,
		filtersKey,
		isFiltered: hasActiveFilters(filters),
		setFilters,
		removeFilters,
		clearFilters,
		page,
		setPage,
		showUnfilteredPage,
	};
}
